import React, { useState, useContext, useEffect } from 'react';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import { FaUser, FaBuilding, FaIndustry, FaKey } from 'react-icons/fa';
import './Account.css';

const AccountSettings = () => {
  const { user, setUser } = useContext(AuthContext);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    industryType: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        organization: user.organization || '',
        industryType: user.industryType || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [user]);

  const { 
    name, 
    email, 
    organization, 
    industryType, 
    currentPassword, 
    newPassword, 
    confirmPassword 
  } = formData;

  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const updateProfile = async e => {
    e.preventDefault();
    setProfileSuccess('');
    setProfileError('');
    setLoading(true);

    try {
      const res = await axios.put(`/api/users/${user.id}`, {
        name,
        email,
        organization,
        industryType
      });
      
      // Update user context
      setUser(res.data);
      
      setProfileSuccess('Profile updated successfully');
      setLoading(false);
    } catch (err) {
      setProfileError(err.response?.data?.msg || 'Failed to update profile');
      setLoading(false);
    }
  };

  const updatePassword = async e => {
    e.preventDefault();
    setPasswordSuccess('');
    setPasswordError('');
    
    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    
    setLoading(true);

    try {
      await axios.put(`/api/users/${user.id}/password`, {
        currentPassword,
        newPassword
      });
      
      setPasswordSuccess('Password updated successfully');
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setLoading(false);
    } catch (err) {
      setPasswordError(err.response?.data?.msg || 'Failed to update password');
      setLoading(false);
    }
  };

  return (
    <div className="account-settings">
      <div className="page-header">
        <div>
          <h1>Account Settings</h1>
          <p>Manage your account information and preferences</p>
        </div>
      </div>

      <div className="account-grid">
        <div className="card account-card">
          <div className="card-header">
            <h2><FaUser /> Profile Information</h2>
          </div>
          {profileSuccess && <div className="alert alert-success">{profileSuccess}</div>}
          {profileError && <div className="alert alert-danger">{profileError}</div>}
          <form onSubmit={updateProfile}>
            <div className="form-group">
              <label htmlFor="name">Full Name</label>
              <input
                type="text"
                name="name"
                id="name"
                value={name}
                onChange={onChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <input
                type="email"
                name="email"
                id="email"
                value={email}
                onChange={onChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="organization">Organization Name</label>
              <div className="input-with-icon">
                <FaBuilding className="input-icon" />
                <input
                  type="text"
                  name="organization"
                  id="organization"
                  value={organization}
                  onChange={onChange}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="industryType">Industry Type</label>
              <div className="input-with-icon">
                <FaIndustry className="input-icon" />
                <select
                  name="industryType"
                  id="industryType"
                  value={industryType}
                  onChange={onChange}
                  required
                >
                  <option value="">Select Industry Type</option>
                  <option value="Financial">Financial</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="College">College</option>
                  <option value="Information Security">Information Security</option>
                  <option value="Retail & E-Commerce">Retail & E-Commerce</option>
                  <option value="Transportation & Logistics">Transportation & Logistics</option>
                  <option value="Non-Profit">Non-Profit</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>
        </div>

        <div className="card account-card">
          <div className="card-header">
            <h2><FaKey /> Change Password</h2>
          </div>
          {passwordSuccess && <div className="alert alert-success">{passwordSuccess}</div>}
          {passwordError && <div className="alert alert-danger">{passwordError}</div>}
          <form onSubmit={updatePassword}>
            <div className="form-group">
              <label htmlFor="currentPassword">Current Password</label>
              <input
                type="password"
                name="currentPassword"
                id="currentPassword"
                value={currentPassword}
                onChange={onChange}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <input
                type="password"
                name="newPassword"
                id="newPassword"
                value={newPassword}
                onChange={onChange}
                required
                minLength="6"
              />
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm New Password</label>
              <input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                value={confirmPassword}
                onChange={onChange}
                required
                minLength="6"
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Change Password'}
            </button>
          </form>
        </div>

        <div className="card account-card">
          <div className="card-header">
            <h2>Subscription Status</h2>
          </div>
          <div className="subscription-info">
            <div className="subscription-status">
              <h3>Current Plan</h3>
              <div className={`status-badge ${user?.subscriptionStatus === 'active' ? 'active' : 'inactive'}`}>
                {user?.subscriptionStatus === 'active' ? 'Active' : 'Inactive'}
              </div>
            </div>
            <p>
              Your Business Resilience Navigator subscription is currently active.
              Payment processing will be added in a future update.
            </p>
            <button className="btn btn-secondary" disabled>
              Manage Subscription
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSettings;
